import type { EditorState } from '#packages/stateManagement';
import type {
  CurrentTpaInnerRoutesInfo,
  IgnoreSubPagesFeature,
  SubPagesToIgnore,
  TpaInnerRouteData,
} from './tpaDynamicPagesReducer';

const getSelectedRouteInfo = (state: EditorState): TpaInnerRouteData =>
  state.tpaDynamicPages.selectedInnerRoute;

const getCurrentRouteInfo = (state: EditorState): CurrentTpaInnerRoutesInfo =>
  state.tpaDynamicPages.currentTpaInfo;

const getInnerRoutesCounts = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
) => state.tpaDynamicPages.innerRoutesCounts?.[appDefinitionId]?.[pageId];

const getAllSubPagesInnerRoutes = (
  state: Partial<EditorState>,
  appDefinitionId: string,
  pageId: string,
): TpaInnerRouteData[] =>
  Object.values(
    state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
      ?.subPages || {},
  )
    .filter((subPageInnerRoutes) => !!subPageInnerRoutes)
    .flat(1);

const getFocusedSubPages = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  feature: IgnoreSubPagesFeature,
): string[] => {
  const allSubPages = Object.keys(
    state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPages ?? {},
  );
  const disabledSubPagesInFeature =
    state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
      ?.subPagesToIgnore?.[feature] ?? [];
  return allSubPages.filter(
    (subPage) => !disabledSubPagesInFeature.includes(subPage),
  );
};

const getFocusedInnerRoutes = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  feature: IgnoreSubPagesFeature,
): TpaInnerRouteData[] => {
  const featureDefinition =
    state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
      ?.subPagesToIgnore?.[feature];
  if (featureDefinition && featureDefinition.length > 0) {
    return Object.entries(
      state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[
        pageId
      ]?.subPages ?? [],
    ).reduce(
      (
        acc: TpaInnerRouteData[],
        [subPage, innerRoutes]: [string, TpaInnerRouteData[]],
      ) => {
        if (!featureDefinition.includes(subPage) && innerRoutes?.length > 0) {
          acc = acc.concat(innerRoutes);
        }
        return acc;
      },
      [],
    );
  }
  return getAllSubPagesInnerRoutes(state, appDefinitionId, pageId);
};

const getSubPageInnerRoute = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
  subPage: string,
) =>
  state.tpaDynamicPages.cachedInnerRoutes?.[appDefinitionId]?.pages?.[pageId]
    ?.subPages?.[subPage];

const getSubPagesFailedToFetch = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
): string[] =>
  state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
    ?.subPages
    ? Object.entries(
        state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[
          pageId
        ].subPages,
      ).reduce((acc, [subPageName, innerRoutes]) => {
        if (!innerRoutes) {
          acc.push(subPageName);
        }
        return acc;
      }, [])
    : null;

const getSubPagesToIgnore = (
  state: EditorState,
  appDefinitionId: string,
  pageId: string,
): SubPagesToIgnore =>
  state.tpaDynamicPages.cachedInnerRoutes[appDefinitionId]?.pages?.[pageId]
    ?.subPagesToIgnore;

const isInFetchProcess = (state: EditorState) =>
  state.tpaDynamicPages.inInnerRoutesFetchProcess;

const getCurrentPageSessionId = (state: EditorState) =>
  state.tpaDynamicPages.currentPageSessionId;

export {
  getSelectedRouteInfo,
  getCurrentRouteInfo,
  getAllSubPagesInnerRoutes,
  getSubPagesFailedToFetch,
  getInnerRoutesCounts,
  getFocusedSubPages,
  getFocusedInnerRoutes,
  getSubPageInnerRoute,
  isInFetchProcess,
  getCurrentPageSessionId,
  getSubPagesToIgnore,
};
